import React from "react";
import Typography from "@material-ui/core/Typography";
import FallbackComponent, { ErrorBoundaryElement } from "./FallbackComponent";

interface Info {
  componentStack: string;
}

type IErrorBoundaryProps = {
  children: React.ReactNode;
  FallbackComponent: ErrorBoundaryElement;
  onError?: (error: Error | null, info: string) => void;
};

type IErrorBoundaryState = {
  readonly error: Error | null;
  readonly info: React.ErrorInfo | null;
};

class ErrorBoundary extends React.Component<
  IErrorBoundaryProps,
  IErrorBoundaryState
> {
  static defaultProps = {
    FallbackComponent: FallbackComponent
  };

  readonly state: IErrorBoundaryState = {
    error: null,
    info: null
  };

  componentDidCatch(error: Error | null, info: React.ErrorInfo) {
    const { onError } = this.props;

    if (typeof onError === "function") {
      try {
        onError.call(this, error, info ? info.componentStack : "");
      } catch (ignoredError) {}
    }

    this.setState({ error, info });
  }

  render() {
    const { children, FallbackComponent } = this.props;
    const { error, info } = this.state;

    if (error !== null) {
      if (process.env.NODE_ENV === "development") {
        return (
          <FallbackComponent
            componentStack={info ? info.componentStack : ""}
            error={error}
          />
        );
      } else if (process.env.NODE_ENV !== "production") {
        return <Typography>Váratlan hiba történt.</Typography>;
      }
    }

    return children || null;
  }
}

export const withErrorBoundary = (
  Component: ErrorBoundaryElement,
  FallbackComponent?: ErrorBoundaryElement,
  onError?: (error: Error | null, info: string) => void
) => {
  const Wrapped = (props: any) => (
    <ErrorBoundary FallbackComponent={FallbackComponent} onError={onError}>
      <Component {...props} />
    </ErrorBoundary>
  );

  const name = Component.displayName || Component.name;

  Wrapped.displayName = name
    ? `WithErrorBoundary(${name})`
    : "WithErrorBoundary";

  return Wrapped;
};

export default ErrorBoundary;
